import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Quote } from "lucide-react";
import { MaskText, FadeUp, EASE } from "../components/Reveal";
import { ProductCard } from "../components/ShopUI";
import { fetchShopProducts } from "../lib/api";
import { IMAGES, FEATURES4, INDUSTRY_CARDS, COLLECTIONS, TESTIMONIALS } from "../data/site";

const CenterHeading = ({ children }) => (
  <div className="text-center mb-14">
    <FadeUp>
      <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-ink">{children}</h2>
      <span className="block w-16 h-px bg-gold mt-5 mx-auto" />
    </FadeUp>
  </div>
);

const Hero = () => (
  <section className="relative min-h-screen overflow-hidden bg-cream" data-testid="hero">
    <motion.img
      src={IMAGES.heroHome}
      alt="The Scent Butler home diffuser on a travertine console in a luxury reception"
      className="absolute inset-0 w-full h-full object-cover object-center"
      initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.5, ease: EASE }}
      data-testid="hero-image"
    />
    <div className="absolute inset-0 hidden md:block pointer-events-none"
      style={{ background: "linear-gradient(to right, rgba(244,238,226,0.97) 0%, rgba(244,238,226,0.91) 28%, rgba(244,238,226,0.45) 50%, rgba(244,238,226,0) 70%)" }} />
    <div className="absolute inset-0 md:hidden pointer-events-none"
      style={{ background: "linear-gradient(to top, rgba(244,238,226,0.98) 0%, rgba(244,238,226,0.85) 36%, rgba(244,238,226,0.12) 66%, rgba(244,238,226,0) 100%)" }} />
    <div className="relative z-10 min-h-screen max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end md:justify-center pb-16 md:pb-0">
      <div className="max-w-xl">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}
          className="eyebrow mb-7">Commercial Scenting · Australia</motion.p>
        <h1 className="font-serif text-6xl sm:text-7xl lg:text-[5.25rem] leading-[0.93] tracking-tight text-ink">
          <MaskText lines={["Scent elevates", "every space."]} delay={0.3} />
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9, ease: EASE }}
          className="text-ink-muted text-lg leading-relaxed mt-8 max-w-md">
          We scent hotels, clinics and showrooms across Australia — and the same diffusers and
          oils are now available for your car and your home.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
          className="flex flex-wrap items-center gap-4 mt-11">
          <Link to="/shop" data-testid="hero-cta-shop"
            className="group inline-flex items-center gap-3 px-9 py-4 bg-[#221d17] text-cream text-xs font-semibold uppercase tracking-[0.2em] hover:bg-black transition-colors duration-300">
            Shop the range
            <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
          <Link to="/enquiries" data-testid="hero-cta-primary"
            className="inline-flex items-center gap-3 px-9 py-4 border border-[#221d17] text-[#221d17] text-xs font-semibold uppercase tracking-[0.2em] hover:bg-[#221d17] hover:text-cream transition-colors duration-300">
            Book a Free Consultation
          </Link>
        </motion.div>
      </div>
    </div>
  </section>
);

const ShopPreview = () => {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    fetchShopProducts().then((r) => setProducts(r.data)).catch(() => {});
  }, []);
  const picks = [
    products.find((p) => p.slug === "car-scent-kit"),
    products.find((p) => p.slug === "home-diffuser"),
    products.find((p) => p.category === "scent"),
  ].filter(Boolean);
  if (picks.length === 0) return null;

  return (
    <section className="bg-cream" data-testid="home-shop-preview">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-28">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <FadeUp>
            <p className="eyebrow mb-5">The Shop</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-ink leading-tight">Take it with you</h2>
            <span className="block w-16 h-px bg-gold mt-5" />
          </FadeUp>
          <FadeUp delay={0.1}>
            <Link to="/shop" data-testid="preview-view-all"
              className="group inline-flex items-center gap-2 text-gold text-xs font-semibold uppercase tracking-[0.2em]">
              View everything <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </FadeUp>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {picks.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
        </div>
      </div>
    </section>
  );
};


const FeatureStrip = () => (
  <section className="bg-cream-alt" data-testid="feature-strip">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <div className="grid grid-cols-2 lg:grid-cols-4">
        {FEATURES4.map((f, i) => (
          <FadeUp key={f.title} delay={i * 0.08}
            className="group flex flex-col items-center text-center gap-4 py-12 px-6 border-cream border-b lg:border-b-0 lg:border-r last:border-r-0"
            data-testid={`feature-${i}`}>
            <f.icon size={28} strokeWidth={1.2} className="text-gold group-hover:scale-110 transition-transform duration-300" />
            <h3 className="font-serif text-lg text-ink leading-snug max-w-[11rem]">{f.title}</h3>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Tailored = () => (
  <section className="bg-cream" data-testid="tailored-section">
    <div className="grid grid-cols-1 lg:grid-cols-5 min-h-[520px]">
      <div className="lg:col-span-2 relative bg-[#221d17] flex items-center overflow-hidden">
        <img src={IMAGES.charcoalMarble} alt="" className="absolute inset-0 w-full h-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-[#1a1611]/70" />
        <div className="relative z-10 px-8 md:px-12 lg:pl-[max(2.5rem,calc((100vw-1400px)/2+2.5rem))] py-20">
          <FadeUp>
            <h2 className="font-serif text-4xl md:text-[2.9rem] leading-[1.05] tracking-tight text-[#f4eee2]">
              Tailored scent solutions for forward thinking businesses.
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="text-[#c9bda9] leading-relaxed mt-6 max-w-sm">
              From a single reception to a whole building, we match the right system and scent to
              your space — then keep it running so you never have to think about it.
            </p>
          </FadeUp>
          <FadeUp delay={0.25}>
            <Link to="/commercial-scenting" data-testid="tailored-cta"
              className="group inline-flex items-center gap-3 mt-9 px-8 py-4 bg-[#d8c9a3] text-[#221d17] text-xs font-semibold uppercase tracking-[0.2em] hover:bg-[#e6dabb] transition-colors duration-300">
              Explore Solutions
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </FadeUp>
        </div>
      </div>
      <div className="lg:col-span-3 relative min-h-[340px] overflow-hidden">
        <img src={IMAGES.lobbyReception} alt="Luxury commercial reception" className="absolute inset-0 w-full h-full object-cover" />
      </div>
    </div>
  </section>
);

const Industries = () => (
  <section className="bg-cream" data-testid="industries-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-28">
      <CenterHeading>Industries we service</CenterHeading>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {INDUSTRY_CARDS.map((ind, i) => (
          <FadeUp key={ind.label} delay={(i % 6) * 0.07}>
            <div className="group relative aspect-[3/4] overflow-hidden" data-testid={`industry-${i}`}>
              <img src={ind.img} alt={ind.label}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#161009]/85 via-[#161009]/20 to-transparent" />
              <span className="absolute bottom-4 left-4 right-4 text-[#f4eee2] text-sm font-serif leading-tight">{ind.label}</span>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Collections = () => (
  <section className="bg-cream-alt" data-testid="collections">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-28">
      <CenterHeading>Signature Collections</CenterHeading>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {COLLECTIONS.map((c, i) => (
          <FadeUp key={c.name} delay={i * 0.12}>
            <Link to="/shop" className="group relative overflow-hidden aspect-[16/10] border border-cream block" data-testid={`collection-${i}`}>
              <img src={c.img} alt={c.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#161009]/85 via-[#161009]/40 to-transparent" />
              <div className="absolute inset-0 p-8 flex flex-col justify-center max-w-[75%]">
                <h3 className="font-serif text-2xl text-[#f4eee2] leading-tight mb-2">{c.name}</h3>
                <p className="text-xs text-[#d8cdba] mb-4 leading-relaxed">{c.tagline}</p>
                <span className="inline-flex items-center gap-2 text-gold text-xs font-semibold uppercase tracking-[0.2em] w-max">
                  Explore <ArrowRight size={13} />
                </span>
              </div>
            </Link>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Testimonials = () => (
  <section className="bg-cream" data-testid="testimonials">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-28">
      <CenterHeading>What our clients say</CenterHeading>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {TESTIMONIALS.map((t, i) => (
          <FadeUp key={i} delay={i * 0.12}
            className="flex flex-col border border-cream-border bg-cream-alt p-9" data-testid={`testimonial-${i}`}>
            <Quote size={26} className="text-gold mb-6" />
            <p className="font-serif text-xl text-ink leading-relaxed flex-1">"{t.quote}"</p>
            <div className="mt-8 pt-6 border-t border-cream-border">
              <p className="text-ink text-sm font-medium">{t.author}</p>
              <p className="text-xs uppercase tracking-[0.14em] text-ink-muted mt-1">{t.org}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const FinalCTA = () => (
  <section className="relative overflow-hidden bg-cream" data-testid="final-cta">
    <div className="absolute inset-0">
      <img src={IMAGES.ctaStone} alt="" className="w-full h-full object-cover object-center" />
      <div className="absolute inset-0 bg-[#f2ece0]/50" />
    </div>
    <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-36 text-center">
      <FadeUp>
        <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-ink leading-tight max-w-3xl mx-auto">
          Create a scent experience <span className="italic">customers remember.</span>
        </h2>
        <span className="block w-16 h-px bg-gold mt-8 mx-auto" />
      </FadeUp>
      <FadeUp delay={0.15}>
        <Link to="/enquiries" data-testid="final-cta-button"
          className="group inline-flex items-center gap-3 mt-10 px-10 py-5 bg-[#221d17] text-cream text-xs font-semibold uppercase tracking-[0.2em] hover:bg-black transition-colors duration-300">
          Book Your Free Consultation
          <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
        </Link>
      </FadeUp>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <FeatureStrip />
      <ShopPreview />
      <Tailored />
      <Industries />
      <Collections />
      <Testimonials />
      <FinalCTA />
    </div>
  );
}
